import { useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useWebsiteV2Sites } from '@shared/utils/websiteV2/useWebsiteV2Sites';
import { useIsWebsiteV2 } from './useIsWebsiteV2';
import { useWebsiteVersionEventQuery } from './websiteVersion.graphql.generated';

const WEBSITE_V2_FLAG = 'useWebsiteV2';

export type UseWebsiteVersionOptions = Readonly<{
  /** The event; defaults to the route's. */
  eventHandle?: string;
  /**
   * Render on the server. The sites a viewer gets depend on their session, which the server render
   * carries only where the route seeds the SSR auth token (the guest site does): off elsewhere.
   */
  ssr?: boolean;
  /**
   * Which V2 site: `selected` (default) is the one the host is working on, as every editor surface reads it;
   * `published` is the one guests are served, for what goes out to them (a link in a message).
   */
  site?: 'selected' | 'published';
  skip?: boolean;
}>;

export type WebsiteVersion = Readonly<{
  /** The event is on Website V2. */
  isWebsiteV2: boolean;
  /** The event's id, once known. */
  eventId: string | undefined;
  /**
   * V2: the event's current site, as `useWebsiteV2Sites` resolves it (the site the host picked, else the one
   * withjoy.com/:handle serves). Null for a V1 event, and for a V2 event with no site this viewer may read
   * (none yet, or, for a guest, none published).
   */
  site: Readonly<{ id: string; isPublished: boolean }> | null;
  isLoading: boolean;
}>;

/**
 * Which website version an event is on and, for Website V2, which of its sites is current. The one source
 * every website adapter reads, so none re-derives either: the version is the event's `useWebsiteV2` flag,
 * and the site follows the ACTIVE-SITE CONTRACT in `useWebsiteV2Sites`.
 *
 * Not for anything `useIsGuestSiteLive` reaches: it makes requests, and those surfaces may render without
 * an Apollo client. They read `useIsWebsiteV2`.
 */
export const useWebsiteVersion = ({ eventHandle, ssr = true, site: siteChoice = 'selected', skip = false }: UseWebsiteVersionOptions = {}): WebsiteVersion => {
  const eventInfo = useEventInfo();
  const routeVersion = useIsWebsiteV2();

  // The route's event resolves from its seeded flags; another event asks for its own.
  const isOtherEvent = !!eventHandle && eventHandle !== eventInfo.eventHandle;
  const { data: otherEventData, loading: isOtherEventLoading } = useWebsiteVersionEventQuery({
    variables: { eventHandle: eventHandle ?? '' },
    batchMode: 'fast',
    skip: skip || !isOtherEvent,
    ssr,
  });
  const otherEvent = otherEventData?.eventByName;

  const isWebsiteV2 = isOtherEvent ? !!otherEvent?.featureFlags?.includes(WEBSITE_V2_FLAG) : routeVersion.isWebsiteV2;
  const isVersionLoading = isOtherEvent ? isOtherEventLoading && !otherEventData : routeVersion.isLoading;
  const eventId = isOtherEvent ? otherEvent?.id : (eventInfo.eventInfo?.eventId ?? eventInfo.ssrEventInfo?.eventId ?? undefined);
  const isEventIdLoading = !eventId && (isOtherEvent ? isOtherEventLoading : eventInfo.loading);

  const sites = useWebsiteV2Sites(eventId ?? '', { skip: skip || !isWebsiteV2, ssr });
  const selectedSite = sites.sites.find(site => (siteChoice === 'published' ? site.isPublished : site.id === sites.selectedSiteId));

  const isLoading = !skip && (isVersionLoading || (isWebsiteV2 && (isEventIdLoading || sites.isLoading)));

  return useMemo(
    () => ({
      isWebsiteV2,
      eventId,
      site: isWebsiteV2 && selectedSite ? { id: selectedSite.id, isPublished: selectedSite.isPublished } : null,
      isLoading,
    }),
    [isWebsiteV2, eventId, selectedSite, isLoading],
  );
};
